import React, { useEffect } from "react";
import { Box, CssBaseline, Typography, keyframes } from "@mui/material";
import ForgotPasswordForm from "../../organisms/Auth/ForgotPasswordForm";

const FONT_HREF =
  "https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700&family=Sora:wght@500;600;700&display=swap";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

const softPulse = keyframes`
  0%, 100% { opacity: 0.35; transform: scale(1); }
  50% { opacity: 0.55; transform: scale(1.04); }
`;

const ForgotPasswordScreen = () => {
  useEffect(() => {
    const existing = document.querySelector(`link[href="${FONT_HREF}"]`);
    if (existing) return undefined;

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = FONT_HREF;
    document.head.appendChild(link);
    return undefined;
  }, []);

  return (
    <>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          fontFamily: '"Manrope", sans-serif',
          bgcolor: "#F5F8FA",
        }}
      >
        {/* Brand panel */}
        <Box
          sx={{
            position: "relative",
            flex: { md: "1 1 48%" },
            minHeight: { xs: 220, md: "100vh" },
            px: { xs: 3, md: 6 },
            py: { xs: 4, md: 6 },
            display: "flex",
            flexDirection: "column",
            justifyContent: { xs: "center", md: "space-between" },
            overflow: "hidden",
            background: `
              radial-gradient(ellipse 80% 60% at 15% 20%, rgba(3, 127, 140, 0.45) 0%, transparent 55%),
              radial-gradient(ellipse 70% 50% at 90% 85%, rgba(2, 40, 64, 0.7) 0%, transparent 50%),
              linear-gradient(155deg, #022840 0%, #025373 48%, #037F8C 100%)
            `,
            color: "#fff",
          }}
        >
          {/* Atmosphere shapes */}
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              width: 280,
              height: 280,
              borderRadius: "50%",
              border: "1px solid rgba(255,255,255,0.12)",
              top: { xs: -80, md: "12%" },
              right: { xs: -60, md: "8%" },
              animation: `${softPulse} 8s ease-in-out infinite`,
            }}
          />
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              width: 160,
              height: 160,
              borderRadius: "50%",
              border: "1px solid rgba(255,255,255,0.08)",
              bottom: { xs: -40, md: "18%" },
              left: { xs: -30, md: "10%" },
              animation: `${softPulse} 10s ease-in-out infinite 1.5s`,
            }}
          />
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              inset: 0,
              opacity: 0.07,
              backgroundImage: `
                linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)
              `,
              backgroundSize: "48px 48px",
              maskImage: "linear-gradient(180deg, black 0%, transparent 85%)",
            }}
          />

          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              animation: `${fadeUp} 0.7s ease-out both`,
            }}
          >
            <Box
              sx={{
                width: { xs: 72, md: 88 },
                height: { xs: 72, md: 88 },
                mb: 2.5,
                borderRadius: 2.5,
                bgcolor: "rgba(255,255,255,0.96)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
              }}
            >
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/assets/Logo-LG-2.png`}
                alt="Litografía Gil"
                sx={{
                  width: "72%",
                  height: "72%",
                  objectFit: "contain",
                }}
              />
            </Box>
            <Typography
              component="p"
              sx={{
                fontFamily: '"Sora", sans-serif',
                fontWeight: 700,
                fontSize: { xs: "1.75rem", md: "2.35rem" },
                letterSpacing: "-0.03em",
                lineHeight: 1.15,
                maxWidth: 360,
              }}
            >
              Litografía Gil
            </Typography>
            <Typography
              sx={{
                mt: 1.5,
                maxWidth: 320,
                fontSize: { xs: "0.95rem", md: "1.05rem" },
                lineHeight: 1.55,
                color: "rgba(255,255,255,0.82)",
                fontWeight: 400,
              }}
            >
              Recupera el acceso a tu cuenta de forma segura.
            </Typography>
          </Box>

          <Typography
            sx={{
              position: "relative",
              zIndex: 1,
              display: { xs: "none", md: "block" },
              mt: 4,
              fontSize: "0.8rem",
              color: "rgba(255,255,255,0.55)",
              animation: `${fadeUp} 0.7s ease-out 0.15s both`,
            }}
          >
            Te enviaremos un enlace a tu correo si la cuenta existe.
          </Typography>
        </Box>

        {/* Form panel */}
        <Box
          sx={{
            flex: { md: "1 1 52%" },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: { xs: 2.5, sm: 4 },
            py: { xs: 4, md: 6 },
            background: `
              radial-gradient(ellipse 60% 40% at 80% 10%, rgba(3, 127, 140, 0.08) 0%, transparent 60%),
              #F5F8FA
            `,
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 420,
              animation: `${fadeUp} 0.75s ease-out 0.12s both`,
            }}
          >
            <ForgotPasswordForm />
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default ForgotPasswordScreen;
